import { Cause, Duration, Effect, Exit, Layer, ManagedRuntime, Scope, Stream } from "effect";
import { RpcClient, RpcClientError } from "effect/unstable/rpc";

import { WS_METHODS } from "@cmmd-forge/contracts";
import { captureWebError } from "../sentry";
import { ClientTracingLive } from "../observability/clientTracing";
import {
  createWsRpcProtocolLayer,
  makeWsRpcProtocolClient,
  type WsProtocolLifecycleHandlers,
  type WsRpcProtocolClient,
  type WsRpcProtocolSocketUrlProvider,
} from "./protocol";
import type { RequestOptions } from "./requestOptions";
import { isRetryableSubscriptionError } from "./wsSubscriptionFiberInterrupt";

interface SubscribeOptions {
  readonly retryDelay?: Duration.Input;
  readonly onResubscribe?: () => void;
  readonly onError?: (error: unknown) => void;
}

interface RequestStreamOptions {
  readonly signal?: AbortSignal;
}

interface SubscriptionState {
  active: boolean;
  hasReceivedValue: boolean;
  cancelCurrentStream: () => void;
}

const DEFAULT_SUBSCRIPTION_RETRY_DELAY_MS = Duration.millis(250);
const NOOP: () => void = () => undefined;

interface TransportSession {
  readonly clientPromise: Promise<WsRpcProtocolClient>;
  readonly clientScope: Scope.Closeable;
  readonly runtime: ManagedRuntime.ManagedRuntime<RpcClient.Protocol, never>;
}

export interface WsTransportProtocol {
  readonly layer: Layer.Layer<RpcClient.Protocol>;
  readonly makeClient: Effect.Effect<
    WsRpcProtocolClient,
    RpcClientError.RpcClientError,
    RpcClient.Protocol | Scope.Scope
  >;
}

export interface WsTransportTiming {
  readonly heartbeatIntervalMs?: number;
  readonly heartbeatTimeoutMs?: number;
  readonly sessionCloseGraceMs?: number;
}

export class WsRequestTimeoutError extends Error {
  override readonly name = "WsRequestTimeoutError";
  readonly timeoutMs: number;

  constructor(timeout: Duration.Input) {
    const timeoutMs = Duration.toMillis(Duration.fromInputUnsafe(timeout));
    super(`WebSocket RPC request timed out after ${timeoutMs}ms.`);
    this.timeoutMs = timeoutMs;
  }
}

/**
 * FORGE-27: classes of RPC stream errors that are expected, not bug-class
 * Sentry events. Currently:
 *  - `OrchestrationGetSnapshotError` fires when the SPA subscribes to a
 *    thread that was deleted in another tab/session before the subscribe
 *    request landed. The SPA's `onError` handler already surfaces the
 *    correct UX (banner + auto-redirect via the sidebar refresh path);
 *    sending the error to Sentry just creates noise.
 *
 * Detection is by `_tag` (Effect schema-encoded tagged-error shape that
 * survives the wire). Caller can still see and handle the error via
 * `options.onError` — we only suppress the Sentry capture.
 */
const EXPECTED_STREAM_ERROR_TAGS = new Set<string>(["OrchestrationGetSnapshotError"]);
function isExpectedRpcStreamError(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const tag = (error as { readonly _tag?: unknown })._tag;
  return typeof tag === "string" && EXPECTED_STREAM_ERROR_TAGS.has(tag);
}

function formatErrorMessage(error: unknown): string {
  const errorMessage = error instanceof Error ? error.message.trim() : "";
  if (errorMessage.length > 0) return errorMessage;
  return error !== null && typeof error === "object" ? formatStructuredError(error) : String(error);
}

function formatStructuredError(error: object): string {
  try {
    return JSON.stringify(error);
  } catch {
    const tagged = error as { readonly _tag?: unknown; readonly message?: unknown };
    const tag = typeof tagged._tag === "string" ? tagged._tag : undefined;
    const message = typeof tagged.message === "string" ? tagged.message : undefined;
    return [tag, message].filter(Boolean).join(": ") || Object.prototype.toString.call(error);
  }
}

// Send a `server.heartbeat` RPC every 4s to keep Effect's client-side
// Socket scope alive. Without real protocol traffic, Effect 4 releases
// the WebSocket with `ws.close(1000)` at ~7s (Socket.js:250), causing a
// reconnect storm under the hood. The interval is small enough to fire
// before Effect's scope timer, large enough to be cheap. See FORGE-216
// and `project_forge_ws_idle_close` memory note for the full diagnosis.
const HEARTBEAT_INTERVAL_MS = 4_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;
const SESSION_CLOSE_GRACE_MS = 500;

export class WsTransport {
  private readonly url: WsRpcProtocolSocketUrlProvider;
  private readonly lifecycleHandlers: WsProtocolLifecycleHandlers | undefined;
  private disposed = false;
  private hasReportedTransportDisconnect = false;
  private reconnectChain: Promise<void> = Promise.resolve();
  private session: TransportSession;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatInFlight = false;
  private readonly protocol: WsTransportProtocol | null;
  private readonly timing: Required<WsTransportTiming>;

  constructor(
    url: WsRpcProtocolSocketUrlProvider,
    lifecycleHandlers?: WsProtocolLifecycleHandlers,
    protocol?: WsTransportProtocol,
    timing?: WsTransportTiming,
  ) {
    this.url = url;
    this.lifecycleHandlers = lifecycleHandlers;
    this.protocol = protocol ?? null;
    this.timing = {
      heartbeatIntervalMs: timing?.heartbeatIntervalMs ?? HEARTBEAT_INTERVAL_MS,
      heartbeatTimeoutMs: timing?.heartbeatTimeoutMs ?? HEARTBEAT_TIMEOUT_MS,
      sessionCloseGraceMs: timing?.sessionCloseGraceMs ?? SESSION_CLOSE_GRACE_MS,
    };
    this.session = this.createSession();
    this.startHeartbeat();
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) return;
    this.heartbeatTimer = setInterval(() => {
      if (this.disposed || this.heartbeatInFlight) return;
      this.heartbeatInFlight = true;
      const heartbeatSession = this.session;
      void this.request((client) => client[WS_METHODS.serverHeartbeat]({}), {
        timeout: this.timing.heartbeatTimeoutMs,
      })
        .catch(async () => {
          if (this.disposed || heartbeatSession !== this.session) return;
          await this.reconnect();
        })
        .finally(() => {
          this.heartbeatInFlight = false;
        });
    }, this.timing.heartbeatIntervalMs);
  }

  async request<TSuccess>(
    execute: (client: WsRpcProtocolClient) => Effect.Effect<TSuccess, Error, never>,
    options?: RequestOptions,
  ): Promise<TSuccess> {
    if (this.disposed) {
      throw new Error("Transport disposed");
    }

    const session = this.session;
    const client = await session.clientPromise;
    const requestEffect = Effect.suspend(() => execute(client));
    const timeout = options?.timeout;
    const boundedRequest =
      timeout === undefined
        ? requestEffect
        : requestEffect.pipe(
            Effect.timeoutOrElse({
              duration: timeout,
              orElse: () => Effect.fail(new WsRequestTimeoutError(timeout)),
            }),
          );
    return await session.runtime.runPromise(boundedRequest, { signal: options?.signal });
  }

  async requestStream<TValue>(
    connect: (client: WsRpcProtocolClient) => Stream.Stream<TValue, Error, never>,
    listener: (value: TValue) => void,
    options?: RequestStreamOptions,
  ): Promise<void> {
    if (this.disposed) {
      throw new Error("Transport disposed");
    }

    const session = this.session;
    const running = this.runStreamOnSession(
      session,
      connect,
      listener,
      () => true,
      () => undefined,
    );
    const abort = () => running.cancel();
    if (options?.signal?.aborted) abort();
    options?.signal?.addEventListener("abort", abort, { once: true });
    try {
      await running.completed;
    } catch (error) {
      if (options?.signal?.aborted) {
        const abortError = new Error("RPC stream request was cancelled.");
        abortError.name = "AbortError";
        throw abortError;
      }
      throw error;
    } finally {
      options?.signal?.removeEventListener("abort", abort);
    }
  }

  subscribe<TValue>(
    connect: (client: WsRpcProtocolClient) => Stream.Stream<TValue, Error, never>,
    listener: (value: TValue) => void,
    options?: SubscribeOptions,
  ): () => void {
    if (this.disposed) {
      return () => undefined;
    }

    const state: SubscriptionState = {
      active: true,
      hasReceivedValue: false,
      cancelCurrentStream: NOOP,
    };
    const retryDelayMs = Duration.toMillis(
      Duration.fromInputUnsafe(options?.retryDelay ?? DEFAULT_SUBSCRIPTION_RETRY_DELAY_MS),
    );
    void this.runSubscriptionLoop(connect, listener, options, state, retryDelayMs);

    return () => {
      state.active = false;
      state.cancelCurrentStream();
    };
  }

  private async runSubscriptionLoop<TValue>(
    connect: (client: WsRpcProtocolClient) => Stream.Stream<TValue, Error, never>,
    listener: (value: TValue) => void,
    options: SubscribeOptions | undefined,
    state: SubscriptionState,
    retryDelayMs: number,
  ): Promise<void> {
    while (state.active && !this.disposed) {
      const session = this.session;
      try {
        this.notifyResubscribe(state, options);
        const runningStream = this.startSubscriptionStream(session, connect, listener, state);
        state.cancelCurrentStream = runningStream.cancel;
        await runningStream.completed;
        state.cancelCurrentStream = NOOP;
      } catch (error) {
        state.cancelCurrentStream = NOOP;
        const shouldContinue = await this.handleSubscriptionFailure(
          error,
          session,
          state,
          options,
          retryDelayMs,
        );
        if (!shouldContinue) return;
      }
    }
  }

  private notifyResubscribe(state: SubscriptionState, options: SubscribeOptions | undefined): void {
    if (!state.hasReceivedValue) return;
    try {
      options?.onResubscribe?.();
    } catch {
      // Reconnect hooks cannot prevent the stream from recovering.
    }
  }

  private startSubscriptionStream<TValue>(
    session: TransportSession,
    connect: (client: WsRpcProtocolClient) => Stream.Stream<TValue, Error, never>,
    listener: (value: TValue) => void,
    state: SubscriptionState,
  ) {
    return this.runStreamOnSession(
      session,
      connect,
      listener,
      () => state.active,
      () => {
        this.hasReportedTransportDisconnect = false;
        state.hasReceivedValue = true;
      },
    );
  }

  private async handleSubscriptionFailure(
    error: unknown,
    session: TransportSession,
    state: SubscriptionState,
    options: SubscribeOptions | undefined,
    retryDelayMs: number,
  ): Promise<boolean> {
    if (!state.active || this.disposed) return false;
    if (session !== this.session) return true;
    const formattedError = formatErrorMessage(error);
    if (!isRetryableSubscriptionError(formattedError)) {
      this.reportNonTransportSubscriptionError(error, options);
      return false;
    }
    if (!this.hasReportedTransportDisconnect) {
      console.warn("WebSocket RPC subscription disconnected", { error: formattedError });
    }
    this.hasReportedTransportDisconnect = true;
    await sleep(retryDelayMs);
    return true;
  }

  private reportNonTransportSubscriptionError(
    error: unknown,
    options: SubscribeOptions | undefined,
  ): void {
    if (!isExpectedRpcStreamError(error)) {
      captureWebError(error, { route: "ws-subscribe", reason: "non-transport-error" });
    }
    options?.onError?.(error);
  }

  async reconnect() {
    if (this.disposed) {
      throw new Error("Transport disposed");
    }

    const reconnectOperation = this.reconnectChain.then(async () => {
      if (this.disposed) {
        throw new Error("Transport disposed");
      }

      const previousSession = this.session;
      this.session = this.createSession();
      await this.closeSession(previousSession);
    });

    this.reconnectChain = reconnectOperation.catch((e) => {
      captureWebError(e, { route: "ws-reconnect" });
    });
    await reconnectOperation;
  }

  async dispose() {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    if (this.heartbeatTimer !== null && this.heartbeatTimer !== undefined) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    await this.closeSession(this.session);
  }

  private async closeSession(session: TransportSession): Promise<void> {
    const gracefulClose = session.runtime.runPromise(Scope.close(session.clientScope, Exit.void));
    let didExceedGracePeriod = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const gracePeriod = new Promise<void>((resolve) => {
      timeoutId = setTimeout(() => {
        didExceedGracePeriod = true;
        resolve();
      }, this.timing.sessionCloseGraceMs);
    });

    try {
      await Promise.race([gracefulClose, gracePeriod]);
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (didExceedGracePeriod) {
        console.warn("WebSocket session close exceeded its grace period; forcing disposal", {
          graceMs: this.timing.sessionCloseGraceMs,
        });
        void gracefulClose.catch(() => undefined);
      }
      await session.runtime.dispose();
    }
  }

  private createSession(): TransportSession {
    const protocol = this.protocol ?? {
      layer: createWsRpcProtocolLayer(this.url, this.lifecycleHandlers),
      makeClient: makeWsRpcProtocolClient,
    };
    const runtime = ManagedRuntime.make(Layer.mergeAll(protocol.layer, ClientTracingLive));
    const clientScope = runtime.runSync(Scope.make());
    return {
      runtime,
      clientScope,
      clientPromise: runtime.runPromise(Scope.provide(clientScope)(protocol.makeClient)),
    };
  }

  private runStreamOnSession<TValue>(
    session: TransportSession,
    connect: (client: WsRpcProtocolClient) => Stream.Stream<TValue, Error, never>,
    listener: (value: TValue) => void,
    isActive: () => boolean,
    markValueReceived: () => void,
  ): {
    readonly cancel: () => void;
    readonly completed: Promise<void>;
  } {
    let resolveCompleted!: () => void;
    let rejectCompleted!: (error: unknown) => void;
    const completed = new Promise<void>((resolve, reject) => {
      resolveCompleted = resolve;
      rejectCompleted = reject;
    });
    const cancel = session.runtime.runCallback(
      Effect.promise(() => session.clientPromise).pipe(
        Effect.flatMap((client) =>
          Stream.runForEach(connect(client), (value) =>
            Effect.sync(() => {
              if (!isActive()) {
                return;
              }

              markValueReceived();
              try {
                listener(value);
              } catch {
                // Swallow listener errors so the stream stays live.
              }
            }),
          ),
        ),
      ),
      {
        onExit: (exit) => {
          if (Exit.isSuccess(exit)) {
            resolveCompleted();
            return;
          }

          rejectCompleted(Cause.squash(exit.cause));
        },
      },
    );

    return {
      cancel,
      completed,
    };
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
